import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Edit, Trash2 } from "react-feather";
import Table from "../../core/pagination/datatable";
import PageHeader from "../components/PageHeader";
import confirmDelete from "../components/confirmDelete";
import JournalModal from "../../core/modals/finance/JournalModal";
import { fetchJournalEntries, deleteJournalEntryThunk } from "../../core/redux/slices/financeSlice";

const today = () => new Date().toISOString().split("T")[0];
const fmt = (v) => `₹${Number(v || 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })}`;

const JournalList = () => {
  const dispatch = useDispatch();
  const { journal, loading } = useSelector((s) => s.finance);
  const [search, setSearch] = useState("");
  const [dateFrom, setDateFrom] = useState(today());
  const [dateTo, setDateTo] = useState(today());
  const [showModal, setShowModal] = useState(false);
  const [editRecord, setEditRecord] = useState(null);

  useEffect(() => {
    dispatch(fetchJournalEntries());
  }, [dispatch]);

  const handleSearch = () => dispatch(fetchJournalEntries());

  // The /api/Financial/journal/all endpoint accepts no query params — date filtering is applied client-side.
  const data = useMemo(() => {
    const q = search.toLowerCase();
    return journal.filter((j) => {
      const matchSearch = !q || j.remarks?.toLowerCase().includes(q) || j.journalNo?.toLowerCase().includes(q);
      const d = j.journalDate ? j.journalDate.split("T")[0] : null;
      const matchDate = (!dateFrom || !d || d >= dateFrom) && (!dateTo || !d || d <= dateTo);
      return matchSearch && matchDate;
    });
  }, [journal, search, dateFrom, dateTo]);

  const openAdd = () => { setEditRecord(null); setShowModal(true); };
  const openEdit = (rec) => { setEditRecord(rec); setShowModal(true); };
  const closeModal = () => { setShowModal(false); setEditRecord(null); dispatch(fetchJournalEntries()); };

  const columns = [
    { title: "Date", dataIndex: "journalDate", width: 110, render: (v) => v ? new Date(v).toLocaleDateString("en-IN") : "—", sorter: (a, b) => (a.journalDate || "").localeCompare(b.journalDate || "") },
    { title: "Reference", dataIndex: "journalNo", width: 150 },
    { title: "Debit A/c", dataIndex: "debitAccountName" },
    {
      title: "Debit", dataIndex: "debitAmount", align: "right", width: 120,
      render: fmt,
    },
    { title: "Credit A/c", dataIndex: "creditAccountName" },
    {
      title: "Credit", dataIndex: "creditAmount", align: "right", width: 120,
      render: fmt,
    },
    { title: "Narration", dataIndex: "remarks", ellipsis: true },
    {
      title: "Action", dataIndex: "action", width: 100,
      render: (_, rec) => (
        <div className="edit-delete-action d-flex">
          <button className="me-2 p-2 btn btn-sm" onClick={() => openEdit(rec)}><Edit size={15} /></button>
          <button className="p-2 btn btn-sm text-danger"
            onClick={() => confirmDelete(() => dispatch(deleteJournalEntryThunk(rec.journalID ?? rec.id)), `journal entry ${rec.journalNo || rec.journalID || rec.id}`)}>
            <Trash2 size={15} />
          </button>
        </div>
      ),
    },
  ];

  return (
    <div className="page-wrapper">
      <div className="content">
        <PageHeader title="Journal Entries" subtitle="Double-entry bookkeeping journal" onAdd={openAdd} addLabel="New Entry" />
        <div className="card table-list-card">
          <div className="card-body">
            <div className="table-top d-flex gap-2 flex-wrap align-items-center mb-2">
              <div className="d-flex align-items-center gap-1">
                <label className="mb-0 small">From</label>
                <input type="date" className="form-control form-control-sm" value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)} style={{ width: 140 }} />
              </div>
              <div className="d-flex align-items-center gap-1">
                <label className="mb-0 small">To</label>
                <input type="date" className="form-control form-control-sm" value={dateTo}
                  onChange={(e) => setDateTo(e.target.value)} style={{ width: 140 }} />
              </div>
              <button className="btn btn-sm btn-primary" onClick={handleSearch}>Search</button>
              <div className="search-input flex-grow-1">
                <input type="text" placeholder="Search narration or reference..."
                  className="form-control form-control-sm formsearch"
                  value={search} onChange={(e) => setSearch(e.target.value)} />
              </div>
            </div>
            <div className="table-responsive">
              <Table columns={columns} dataSource={data} loading={loading} />
            </div>
          </div>
        </div>
      </div>
      <JournalModal show={showModal} onClose={closeModal} editRecord={editRecord} />
    </div>
  );
};

export default JournalList;
